import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  AlertTriangle,
  Building2,
  Phone,
  Mail,
  Briefcase,
  Shield,
  User,
  X,
  Loader2,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import Modal from './WaModal';
import { api } from '../../services/api';

interface Empresa {
  id: number;
  nome: string;
  cnpj: string;
}

const DEPARTAMENTOS = [
  { id: 'fiscal', label: 'Fiscal' },
  { id: 'contabil', label: 'Contábil' },
  { id: 'pessoal', label: 'Depto. Pessoal' },
  { id: 'societario', label: 'Societário' },
  { id: 'financeiro', label: 'Financeiro' },
  { id: 'consultoria', label: 'Consultoria' },
];

interface ContactFormData {
  nome: string;
  telefone: string;
  email: string;
  empresaIds: number[];
  cargo: string;
  departamentos: string[];
  tipo: 'PF' | 'PJ';
  principal: boolean;
  observacoes: string;
  // Se true, o bot do WhatsApp não pede avaliação ao finalizar (issue #42).
  avaliacaoDesativada: boolean;
}

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ContactFormData) => void;
  initialPhone?: string;
  initialName?: string;
  /** Quando passado, pré-seleciona a empresa e esconde o seletor */
  empresaId?: number;
  /** Modo edição: ID do contato a editar */
  editContactId?: number;
  /** Dados iniciais para edição */
  editData?: {
    nome?: string;
    telefone?: string;
    email?: string;
    cargo?: string;
    departamento?: string;
    empresaId?: number;
    principal?: boolean;
    avaliacaoDesativada?: boolean;
  };
}

// Validação local de nome (regex - zero custo, antes de chamar IA)
function isNameValid(name: string): { valid: boolean; reason?: string } {
  const trimmed = name.trim();
  if (!trimmed) return { valid: false, reason: 'Nome é obrigatório' };
  if (trimmed.length < 3) return { valid: false, reason: 'Nome muito curto' };

  // Rejeitar números puros ou telefones
  if (/^[\d\s+\-()]+$/.test(trimmed)) {
    return { valid: false, reason: 'Nome não pode ser apenas números. Informe o nome da pessoa.' };
  }

  // Rejeitar padrões comuns de WhatsApp
  const rejectPatterns = [
    /^\+?\d{10,}$/, // +5511999887766
    /^whatsapp/i, // WhatsApp User
    /^número/i, // Número Novo
    /^contato/i, // Contato
    /^novo$/i, // Novo
    /^user$/i, // User
    /^cliente$/i, // Cliente
    /^(\d{2,3})\s?\d{4,5}/, // 11 99988
  ];

  const matchedPattern = rejectPatterns.some(pattern => pattern.test(trimmed));
  if (matchedPattern) {
    return {
      valid: false,
      reason:
        'Nome não identificável. Informe nome e sobrenome da pessoa ou razão social da empresa.',
    };
  }

  // Verificar se tem pelo menos 2 palavras (nome + sobrenome) ou parece empresa
  const words = trimmed.split(/\s+/).filter(w => w.length > 1);
  if (words.length < 2) {
    // Pode ser nome de empresa (ex: "Tech Solutions")
    // Se tiver apenas 1 palavra, verificar se parece válido
    if (trimmed.length < 5) {
      return {
        valid: false,
        reason: 'Informe o nome completo (nome e sobrenome) ou razão social.',
      };
    }
  }

  return { valid: true };
}

export default function ContactFormModal({
  isOpen,
  onClose,
  onSave,
  initialPhone,
  initialName,
  empresaId,
  editContactId,
  editData,
}: ContactFormModalProps) {
  const isEditing = !!editContactId;
  const [form, setForm] = useState<ContactFormData>({
    nome: editData?.nome || initialName || '',
    telefone: editData?.telefone || initialPhone || '',
    email: editData?.email || '',
    empresaIds: (() => {
      if (editData?.empresaId) return [editData.empresaId];
      if (empresaId) return [empresaId];
      return [];
    })(),
    cargo: editData?.cargo || '',
    departamentos: editData?.departamento ? [editData.departamento] : [],
    tipo: 'PF',
    principal: editData?.principal ?? false,
    observacoes: '',
    avaliacaoDesativada: editData?.avaliacaoDesativada ?? false,
  });
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [empresaSearch, setEmpresaSearch] = useState('');
  const [showEmpresaDropdown, setShowEmpresaDropdown] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // Buscar empresas da base real
  const fetchEmpresas = useCallback(async (search: string) => {
    setLoadingEmpresas(true);
    try {
      const params = new URLSearchParams({ size: '20' });
      if (search) params.set('search', search);
      const res = await api.get(`/api/v1/empresas/?${params.toString()}`);
      const data = res.data?.data || res.data;
      const items = data?.items || data?.results || (Array.isArray(data) ? data : []);
      setEmpresas(
        items.map((e: any) => ({
          id: e.id,
          nome: e.nome || e.razao_social || '',
          cnpj: e.cnpj || '',
        })),
      );
    } catch {
      // Fallback vazio
      setEmpresas([]);
    }
    setLoadingEmpresas(false);
  }, []);

  // Carregar empresas ao abrir o modal
  useEffect(() => {
    if (isOpen) fetchEmpresas('');
  }, [isOpen, fetchEmpresas]);

  // Preencher form com dados de edição quando o modal abre
  // Depende de editContactId (estável) em vez de editData (objeto recriado a cada render)
  useEffect(() => {
    if (isOpen && editContactId && editData) {
      setForm({
        nome: editData.nome || '',
        telefone: editData.telefone || '',
        email: editData.email || '',
        empresaIds: editData.empresaId ? [editData.empresaId] : [],
        cargo: editData.cargo || '',
        departamentos: editData.departamento ? [editData.departamento] : [],
        tipo: 'PF',
        principal: editData.principal ?? false,
        observacoes: '',
        avaliacaoDesativada: editData.avaliacaoDesativada ?? false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editContactId]);

  // Buscar com debounce ao digitar
  useEffect(() => {
    if (!showEmpresaDropdown) return undefined;
    const timer = setTimeout(() => fetchEmpresas(empresaSearch), 300);
    return () => clearTimeout(timer);
  }, [empresaSearch, showEmpresaDropdown, fetchEmpresas]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectedEmpresas = empresas.filter(e => form.empresaIds.includes(e.id));

  const toggleEmpresa = (id: number) => {
    setForm(f => ({
      ...f,
      empresaIds: f.empresaIds.includes(id)
        ? f.empresaIds.filter(eid => eid !== id)
        : [...f.empresaIds, id],
    }));
    setErrors(e => ({ ...e, empresas: '' }));
  };

  const toggleDepartamento = (id: string) => {
    setForm(f => ({
      ...f,
      departamentos: f.departamentos.includes(id)
        ? f.departamentos.filter(d => d !== id)
        : [...f.departamentos, id],
    }));
    setErrors(e => ({ ...e, departamentos: '' }));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    // Nome
    const nameCheck = isNameValid(form.nome);
    if (!nameCheck.valid) {
      newErrors.nome = nameCheck.reason!;
      setNameError(nameCheck.reason!);
    } else {
      setNameError(null);
    }

    // Empresa obrigatória (pula se já veio via prop)
    if (!empresaId && form.empresaIds.length === 0) {
      newErrors.empresas = 'Selecione pelo menos uma empresa';
    }

    // Departamentos obrigatórios (pula se veio da tela de empresa)
    if (!empresaId && form.departamentos.length === 0) {
      newErrors.departamentos = 'Selecione pelo menos um departamento';
    }

    // Telefone
    if (!form.telefone.trim()) {
      newErrors.telefone = 'Telefone é obrigatório';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        telefone: form.telefone.trim(),
        email: form.email.trim() || null,
        cargo: form.cargo.trim() || null,
        departamento: form.departamentos[0] || null,
        tipo: 'contato' as const,
        principal: form.principal,
        observacoes: form.observacoes.trim() || null,
        avaliacao_desativada: form.avaliacaoDesativada,
      };

      if (isEditing) {
        // Modo edição: PUT no contato existente
        const eid = form.empresaIds[0] || editData?.empresaId;
        if (!eid) {
          toast.error('Empresa não identificada. Selecione uma empresa para salvar.');
          return;
        }
        await api.put(`/api/v1/empresas/${eid}/contatos/${editContactId}/`, payload);
        toast.success('Contato atualizado com sucesso!');
      } else {
        // Modo criação: POST para cada empresa
        const promises = form.empresaIds.map(eid =>
          api.post(`/api/v1/empresas/${eid}/contatos/`, payload),
        );
        await Promise.all(promises);
        toast.success('Contato cadastrado com sucesso!');
      }
      onSave(form);
      handleClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message;
      const fallback = isEditing ? 'Erro ao atualizar contato' : 'Erro ao cadastrar contato';
      toast.error(typeof detail === 'string' ? detail : fallback);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm({
      nome: initialName || '',
      telefone: initialPhone || '',
      email: '',
      empresaIds: empresaId ? [empresaId] : [],
      cargo: '',
      departamentos: [],
      tipo: 'PF',
      principal: false,
      observacoes: '',
      avaliacaoDesativada: false,
    });
    setErrors({});
    setNameError(null);
    setEmpresaSearch('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Editar Contato' : 'Cadastrar Contato'}
      size="lg"
    >
      <div className="space-y-5">
        {/* Nome */}
        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="contact-nome" className="mb-1 block text-sm font-medium text-gray-700">
            Nome *
          </label>
          <input
            id="contact-nome"
            type="text"
            value={form.nome}
            onChange={e => {
              setForm(f => ({ ...f, nome: e.target.value }));
              setNameError(null);
              setErrors(er => ({ ...er, nome: '' }));
            }}
            placeholder="Nome completo ou razão social"
            className={clsx(
              'w-full rounded-xl border px-4 py-2.5 text-sm transition-all focus:outline-none focus:ring-2',
              errors.nome
                ? 'border-red-300 bg-red-50 focus:ring-red-300'
                : 'border-gray-200 focus:ring-green-400',
            )}
          />
          {nameError && (
            <div className="mt-1.5 flex items-start gap-2 text-red-600">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <span className="text-xs">{nameError}</span>
            </div>
          )}
        </div>

        {/* Telefone + Email lado a lado */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor="contact-telefone"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              <Phone size={13} className="mr-1 inline" />
              Telefone *
            </label>
            <input
              id="contact-telefone"
              type="text"
              value={form.telefone}
              onChange={e => {
                setForm(f => ({ ...f, telefone: e.target.value }));
                setErrors(er => ({ ...er, telefone: '' }));
              }}
              placeholder="5511999887766"
              className={clsx(
                'w-full rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-2',
                errors.telefone
                  ? 'border-red-300 bg-red-50 focus:ring-red-300'
                  : 'border-gray-200 focus:ring-green-400',
              )}
            />
            {errors.telefone && (
              <span className="mt-1 text-xs text-red-500">{errors.telefone}</span>
            )}
          </div>
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="contact-email" className="mb-1 block text-sm font-medium text-gray-700">
              <Mail size={13} className="mr-1 inline" />
              Email
            </label>
            <input
              id="contact-email"
              type="email"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="email@empresa.com"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>
        </div>

        {/* Empresa(s) - obrigatório (esconde se empresaId veio via prop) */}
        {!empresaId && (
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label
              htmlFor="contact-empresa-search"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              <Building2 size={13} className="mr-1 inline" />
              Empresa(s) vinculada(s) *
            </label>

            {/* Empresas selecionadas */}
            {selectedEmpresas.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {selectedEmpresas.map(emp => (
                  <span
                    key={emp.id}
                    className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700"
                  >
                    {emp.nome}
                    <button
                      type="button"
                      onClick={() => toggleEmpresa(emp.id)}
                      className="hover:text-red-500"
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Search empresas */}
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                id="contact-empresa-search"
                type="text"
                value={empresaSearch}
                onChange={e => {
                  setEmpresaSearch(e.target.value);
                  setShowEmpresaDropdown(true);
                }}
                onFocus={() => setShowEmpresaDropdown(true)}
                placeholder="Buscar empresa por nome ou CNPJ..."
                className={clsx(
                  'w-full rounded-xl border py-2.5 pl-9 pr-4 text-sm focus:outline-none focus:ring-2',
                  errors.empresas
                    ? 'border-red-300 bg-red-50 focus:ring-red-300'
                    : 'border-gray-200 focus:ring-green-400',
                )}
              />
              {errors.empresas && (
                <span className="mt-1 text-xs text-red-500">{errors.empresas}</span>
              )}

              {showEmpresaDropdown && (
                <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-40 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                  {empresas.map(emp => {
                    const selected = form.empresaIds.includes(emp.id);
                    return (
                      <button
                        type="button"
                        key={emp.id}
                        onClick={() => {
                          toggleEmpresa(emp.id);
                          setEmpresaSearch('');
                          setShowEmpresaDropdown(false);
                        }}
                        className={clsx(
                          'flex w-full items-center justify-between px-3 py-2 text-sm transition-colors hover:bg-gray-50',
                          selected && 'bg-green-50',
                        )}
                      >
                        <div className="text-left">
                          <span
                            className={clsx(
                              'font-medium',
                              selected ? 'text-green-700' : 'text-gray-800',
                            )}
                          >
                            {emp.nome}
                          </span>
                          <span className="ml-2 text-xs text-gray-400">{emp.cnpj}</span>
                        </div>
                        {selected && <span className="text-xs font-bold text-green-600">✓</span>}
                      </button>
                    );
                  })}
                  {loadingEmpresas && (
                    <div className="flex items-center justify-center gap-2 px-3 py-4 text-sm text-gray-400">
                      <Loader2 size={14} className="animate-spin" />
                      Buscando empresas...
                    </div>
                  )}
                  {!loadingEmpresas && empresas.length === 0 && (
                    <div className="px-3 py-4 text-center text-sm text-gray-400">
                      Nenhuma empresa encontrada
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cargo + Tipo */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="contact-cargo" className="mb-1 block text-sm font-medium text-gray-700">
              <Briefcase size={13} className="mr-1 inline" />
              Cargo
            </label>
            <input
              id="contact-cargo"
              type="text"
              value={form.cargo}
              onChange={e => setForm(f => ({ ...f, cargo: e.target.value }))}
              placeholder="Ex: Diretor, Contador, Gerente"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>
          <div>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor="contact-tipo" className="mb-1 block text-sm font-medium text-gray-700">
              <User size={13} className="mr-1 inline" />
              Tipo
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, tipo: 'PF' }))}
                className={clsx(
                  'flex-1 rounded-xl border py-2.5 text-sm font-medium transition-all',
                  form.tipo === 'PF'
                    ? 'border-green-600 bg-green-600 text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
                )}
              >
                Pessoa Física
              </button>
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, tipo: 'PJ' }))}
                className={clsx(
                  'flex-1 rounded-xl border py-2.5 text-sm font-medium transition-all',
                  form.tipo === 'PJ'
                    ? 'border-green-600 bg-green-600 text-white'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50',
                )}
              >
                Pessoa Jurídica
              </button>
            </div>
          </div>
        </div>

        {/* Departamentos liberados - obrigatório */}
        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor="contact-departamentos"
            className="mb-2 block text-sm font-medium text-gray-700"
          >
            <Shield size={13} className="mr-1 inline" />
            Departamentos liberados *
          </label>
          <div className="flex flex-wrap gap-2">
            {DEPARTAMENTOS.map(dept => {
              const selected = form.departamentos.includes(dept.id);
              return (
                <button
                  type="button"
                  key={dept.id}
                  onClick={() => toggleDepartamento(dept.id)}
                  className={clsx(
                    'rounded-full border px-3 py-1.5 text-xs font-medium transition-all',
                    selected
                      ? 'border-green-600 bg-green-600 text-white'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-green-300 hover:text-green-600',
                  )}
                >
                  {dept.label}
                </button>
              );
            })}
          </div>
          {errors.departamentos && (
            <span className="mt-1 text-xs text-red-500">{errors.departamentos}</span>
          )}
        </div>

        {/* Contato principal + Observações */}
        <div className="flex items-center gap-3">
          <label htmlFor="contact-principal" className="flex cursor-pointer items-center gap-2">
            <input
              id="contact-principal"
              type="checkbox"
              checked={form.principal}
              onChange={e => setForm(f => ({ ...f, principal: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm text-gray-600">Contato principal da empresa</span>
          </label>
        </div>

        {/* Avaliação ao finalizar atendimento (issue #42) */}
        <div className="flex flex-col gap-1">
          <label htmlFor="contact-avaliacao" className="flex cursor-pointer items-center gap-2">
            <input
              id="contact-avaliacao"
              type="checkbox"
              checked={form.avaliacaoDesativada}
              onChange={e => setForm(f => ({ ...f, avaliacaoDesativada: e.target.checked }))}
              className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
            />
            <span className="text-sm text-gray-600">
              Desativar avaliação ao finalizar atendimento
            </span>
          </label>
          <span className="pl-6 text-xs text-gray-400">
            Quando marcado, o bot não envia a pesquisa de avaliação a este contato ao encerrar o
            atendimento.
          </span>
        </div>

        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label
            htmlFor="contact-observacoes"
            className="mb-1 block text-sm font-medium text-gray-700"
          >
            Observações
          </label>
          <textarea
            id="contact-observacoes"
            value={form.observacoes}
            onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
            placeholder="Informações adicionais sobre o contato..."
            rows={2}
            className="w-full resize-none rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
          />
        </div>

        {/* Botões */}
        <div className="flex justify-end gap-3 border-t border-gray-100 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-xl bg-gray-100 px-5 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-200"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-xl bg-green-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : 'Cadastrar Contato'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
